// services/otc/market/engine.ts
// (adjust the path if you keep it under utils/ or store/)

import { numberUtils } from '@rabex-kit/rabex-ui'; // ← update to your actual path
import type { Market } from './types';

export type Changed = 'pay' | 'receive';

export interface CalcInput {
  /** which input the user last changed */
  changed: Changed;
  /** raw, cleaned strings (no separators) coming from inputs */
  pay: string;
  receive: string;
}

export interface CalcOut {
  /** rounded strings ready for UI */
  payAmount: string;
  receiveAmount: string;
  commission: string; // fee in receive side units
  userReceiveAmount: string; // receiveAmount - commission
  valid: boolean; // basic validity (price > 0 and amounts > 0)
}

function toNum(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Pure calculator.
 *
 * Direction:
 *  - If `payIsQuote = true` → paying QUOTE, receiving BASE (BUY)
 *  - If `payIsQuote = false` → paying BASE, receiving QUOTE (SELL/EXCHANGE)
 *
 * Rounding:
 *  - Pay amount rounds with the precision of the asset you’re paying in
 *  - Receive amount rounds with the precision of the asset you’re receiving in
 *  - Commission applies to the receive side, then we round it with the same precision
 */
// Performance Checked and almost near 0.2 ms and it is good
export function calc(
  market: Market,
  price: number | undefined,
  changed: Changed,
  payIsQuote: boolean,
  input: CalcInput
): CalcOut {
  const p = price ?? 0;
  const hasPrice = p > 0;

  // figure out which precision applies to pay/receive sides
  const payPrec = payIsQuote ? market.precisions.quote : market.precisions.base;
  const recvPrec = payIsQuote
    ? market.precisions.base
    : market.precisions.quote;

  // compute raw amounts
  let pay = 0;
  let recv = 0;

  if (changed === 'pay') {
    pay = toNum(input.pay);
    if (hasPrice) {
      recv = payIsQuote ? pay / p : pay * p;
    }
  } else {
    recv = toNum(input.receive);
    if (hasPrice) {
      pay = payIsQuote ? recv * p : recv / p;
    }
  }

  // round amounts once, deterministically
  // What the user typed is kept as typed; only a derived pay amount is rounded
  // up (so the user never underpays), which could otherwise add one unit.
  const payRounded =
    changed === 'pay'
      ? numberUtils.roundDown(pay, payPrec)
      : numberUtils.roundUp(pay, payPrec);
  const recvRounded = numberUtils.roundDown(recv, recvPrec);

  // fee on receive side
  const feeFrac =
    market.fees.commissionFraction ?? (market.fees.commissionPct ?? 0) / 100;
  const feeRaw = +recvRounded * (feeFrac || 0);
  const fee = numberUtils.roundUp(feeRaw, recvPrec);
  // Built from the two rounded figures shown, so receive − commission adds up.
  const userR = numberUtils.roundDown(+recvRounded - +fee, recvPrec);

  const valid = hasPrice && +payRounded > 0 && +recvRounded > 0;
  return {
    payAmount: String(payRounded),
    receiveAmount: String(recvRounded),
    commission: String(fee),
    userReceiveAmount: String(userR),
    valid
  };
}
